import { useEffect, useRef, useState } from 'react'
import { LucideIcon } from 'lucide-react'
import { Card, CardContent } from '@/components/ui'
import { cn } from '@/lib/utils'

interface StatsCardProps {
  title: string
  value: string | number
  icon: LucideIcon
  description?: string
  trend?: 'up' | 'down' | 'neutral'
  alert?: boolean
  className?: string
}

/**
 * Animate a number from 0 to target over ~600ms.
 * For string values (e.g. "2.5"), animates the numeric part.
 */
function useCountUp(target: string | number): string {
  const numericTarget = typeof target === 'number' ? target : parseFloat(target)
  const isFloat = typeof target === 'string' && target.includes('.')
  const [current, setCurrent] = useState(0)
  const frameRef = useRef<number>(0)
  const startRef = useRef<number>(0)

  useEffect(() => {
    if (isNaN(numericTarget) || numericTarget === 0) {
      setCurrent(0)
      return
    }

    const duration = 600
    startRef.current = performance.now()

    function step(timestamp: number) {
      const elapsed = timestamp - startRef.current
      const progress = Math.min(elapsed / duration, 1)
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3)
      setCurrent(eased * numericTarget)

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(step)
      }
    }

    frameRef.current = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frameRef.current)
  }, [numericTarget])

  if (isNaN(numericTarget)) return String(target)
  if (isFloat) return current.toFixed(1)
  return String(Math.round(current))
}

export function StatsCard({
  title,
  value,
  icon: Icon,
  description,
  alert,
  className,
}: StatsCardProps) {
  const displayValue = useCountUp(value)

  return (
    <Card className={cn('hover:border-border/80 hover:shadow-md transition-all duration-200', alert && 'border-destructive/30 shadow-[0_0_12px_-3px] shadow-destructive/20', className)}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className={cn('text-3xl font-bold tracking-tight', alert && 'text-destructive')}>
              {displayValue}
            </p>
            {description && (
              <p className="text-xs text-muted-foreground">{description}</p>
            )}
          </div>
          <div
            className={cn(
              'h-10 w-10 rounded-xl flex items-center justify-center',
              alert ? 'bg-gradient-to-br from-destructive/10 to-destructive/5' : 'bg-gradient-to-br from-primary/10 to-primary/5'
            )}
          >
            <Icon
              className={cn(
                'h-5 w-5',
                alert ? 'text-destructive' : 'text-primary'
              )}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
